import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { Alert, Box, Stack, Text, Select } from '@mantine/core';

import { MENU_OBJECT_TYPES } from 'constants/menuManagement';

import { fetchCubohMenus, fetchResourcePOS, getTargetItemsFromMenu } from 'helpers/menuManagement';

import { useAuth } from 'hooks/useAuth';
import { useTranslation } from 'i18n';

const NS = 'menuManagementCrossMenuDuplication';

export default function CrossMenuDuplicateSelector({
  selectType,
  context,
  hidden,
  disabled,
  recordCompanyId,
}) {
  const { t } = useTranslation(NS);

  const { selectedOptions, setSelectedOptions, selectItemData, setSelectItemData, record } =
    useContext(context);

  const { companies, merchants } = useAuth();

  const [isLoading, setIsLoading] = useState(false);

  // eslint-disable-next-line consistent-return
  const formattedCompanies = useMemo(async () => {
    if (selectType === 'companyId') {
      // eslint-disable-next-line no-restricted-syntax, guard-for-in, no-unreachable-loop
      for (const companyId in companies) {
        // eslint-disable-next-line max-len
        // a company is selectable if it is the same as the source, or if it is different but does not use POS shooter
        if (companyId !== recordCompanyId) {
          // eslint-disable-next-line no-await-in-loop
          const posDetails = await fetchResourcePOS(companyId);
          if (posDetails) {
            return {
              ...companies,
              [companyId]: {
                ...companies[companyId],
                isShooterCompany: posDetails.use_mm_endpoint,
              },
            };
          }
        }
        return {
          ...companies,
          [companyId]: {
            ...companies[companyId],
            isShooterCompany: false,
          },
        };
      }
    } else {
      return companies;
    }
  }, [companies, recordCompanyId, selectType]);

  // eslint-disable-next-line no-shadow
  const populateOptions = async ({ selectType, selectedOptions, companies, merchants, record }) => {
    let selectOptions;
    switch (selectType) {
      case 'companyId':
        selectOptions = Object.values(companies).map(company => ({
          value: String(company.id),
          label: company.name,
          disabled: company.isShooterCompany,
        }));
        break;
      case 'merchantId':
        if (!selectedOptions.companyId) return [];
        selectOptions = Object.values(merchants)
          .filter(merchant => merchant.companyID === selectedOptions.companyId)
          .map(merchant => ({
            value: String(merchant.id),
            label: merchant.name,
          }));
        break;
      case MENU_OBJECT_TYPES.menus: {
        if (!selectedOptions.merchantId) return [];

        const menus = await fetchCubohMenus({
          companyID: selectedOptions.companyId,
          merchantID: selectedOptions.merchantId,
          isParentCompanyUser: true,
        });

        if (menus.error) {
          setSelectItemData(prev => ({
            ...prev,
            [MENU_OBJECT_TYPES.menus]: {
              ...prev[MENU_OBJECT_TYPES.menus],
              error: menus.error,
            },
          }));
          return [];
        }

        const merchantMenus = menus?.[parseInt(selectedOptions.merchantId, 10)];

        selectOptions = merchantMenus
          ?.filter(menu => !menu.deleted)
          .map(menu => ({
            value: String(menu?.uuid),
            label: menu.name,
            disabled: record.parentMenus.includes(menu?.uuid) || menu.masterMerchant?.merchantId,
          }));
        break;
      }
      case MENU_OBJECT_TYPES.categories:
        if (!selectedOptions[MENU_OBJECT_TYPES.menus]) return [];
        selectOptions = await getTargetItemsFromMenu({
          companyId: selectedOptions.companyId,
          merchantId: selectedOptions.merchantId,
          itemType: MENU_OBJECT_TYPES.categories,
          parentUuid: selectedOptions[MENU_OBJECT_TYPES.menus],
        });
        selectOptions = selectOptions?.map(item => ({
          value: item?.uuid,
          label: item?.name,
        }));
        break;
      case MENU_OBJECT_TYPES.items:
        if (!selectedOptions[MENU_OBJECT_TYPES.categories]) return [];
        selectOptions = await getTargetItemsFromMenu({
          companyId: selectedOptions.companyId,
          merchantId: selectedOptions.merchantId,
          itemType: MENU_OBJECT_TYPES.items,
          parentUuid: selectedOptions[MENU_OBJECT_TYPES.categories],
        });
        selectOptions = selectOptions?.map(item => ({
          value: item?.uuid,
          label: item.name,
        }));
        break;
      case MENU_OBJECT_TYPES.modifierGroups:
        if (!selectedOptions[MENU_OBJECT_TYPES.items]) return [];
        selectOptions = await getTargetItemsFromMenu({
          companyId: selectedOptions.companyId,
          merchantId: selectedOptions.merchantId,
          itemType: MENU_OBJECT_TYPES.modifierGroups,
          parentUuid: selectedOptions[MENU_OBJECT_TYPES.items],
        });
        selectOptions = selectOptions?.map(item => ({
          value: item?.uuid,
          label: item?.name,
        }));
        break;
      default:
        selectOptions = [];
        break;
    }
    return selectOptions;
  };

  const fetchOptions = useCallback(async () => {
    setIsLoading(true);
    let options = [];
    try {
      options = await populateOptions({
        selectType,
        selectedOptions,
        companies: await formattedCompanies,
        merchants,
        record,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setSelectItemData(existingState => ({
        ...existingState,
        [selectType]: {
          ...existingState[selectType],
          options,
        },
      }));
      setIsLoading(false);
    }
  }, [formattedCompanies, merchants, selectType, selectedOptions, setSelectItemData]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const handleChange = value => {
    // for all the options further down the chain of selectedOptions, reset them to null
    const resetFields = Object.keys(selectedOptions).splice(
      Object.keys(selectedOptions).indexOf(selectType) + 1,
    );
    const updatedSelectedOptions = { ...selectedOptions };
    resetFields.forEach(field => {
      updatedSelectedOptions[field] = null;
    });
    updatedSelectedOptions[selectType] = ['companyId', 'merchantId'].includes(selectType)
      ? parseInt(value, 10)
      : value;
    setSelectedOptions(updatedSelectedOptions);

    // Reset options for dependent fields
    setSelectItemData(prev => {
      const updatedState = { ...prev };
      resetFields.forEach(field => {
        updatedState[field] = {
          ...prev[field],
          options: [],
        };
      });
      return updatedState;
    });
  };

  const noOptionsFound = !isLoading && !disabled && !selectItemData[selectType]?.options?.length;

  const formatSelectOptions = (options = []) =>
    options
      ?.filter(option => option !== null && option !== undefined)
      .map(option => ({
        value: option.value?.toString() || '',
        label: option.label || '',
        disabled: option.disabled || false,
      }));

  if (hidden) {
    return null;
  }

  return (
    <Box mb="lg">
      <Text variant="sublabel-sm" mb="xs2">
        {selectItemData[selectType].title}
      </Text>
      <Stack>
        {noOptionsFound ? (
          <Alert variant="caution">{selectItemData[selectType].noDataMessage}</Alert>
        ) : (
          <Select
            disabled={disabled}
            value={selectedOptions[selectType]?.toString()}
            onChange={handleChange}
            placeholder={isLoading ? t('CrossMenuDuplicateSelector.loadingPlaceholder') : selectItemData[selectType].placeholder}
            data={formatSelectOptions(selectItemData[selectType]?.options)}
            size="md"
          />
        )}
      </Stack>
    </Box>
  );
}

CrossMenuDuplicateSelector.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  context: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  recordCompanyId: PropTypes.string,
  selectType: PropTypes.string.isRequired,
};
